import { useEffect, useMemo, useState } from "react";
import { useVisualFormContext } from "../contexts/VisualFormContext";
import { useDashboardDomainContext } from "../DashboardDomainProvider";

export const useLayoutMouseEvents = () => {

    const { dashboardLib, selectedTabData, isViewMode } = useDashboardDomainContext();
    const { openVisualCreation } = useVisualFormContext();

    const [startingCoordinates, setStartingCoordinates] = useState(null);
    const [hoverCoordinates, setHoverCoordinates] = useState(null);

    const [isDragging, setIsDragging] = useState(false);

    const boundingBox = useMemo(() => startingCoordinates && hoverCoordinates ? {
        i: `bounding-box-grid`,
        x: Math.min(startingCoordinates.x, hoverCoordinates.x),
        y: Math.min(startingCoordinates.y, hoverCoordinates.y),
        w: Math.abs(startingCoordinates.x - hoverCoordinates.x) + 1,
        h: Math.abs(startingCoordinates.y - hoverCoordinates.y) + 1,
    } : null, [startingCoordinates, hoverCoordinates]);

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (!isViewMode && startingCoordinates) {
                setStartingCoordinates(null);
                setHoverCoordinates(null);
            }
        }

        window.addEventListener('mouseup', handleGlobalMouseUp)
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp)
        }
    }, [startingCoordinates, isViewMode]);

    const isBoxColliding = (box1, box2 = boundingBox) => {
        
        if (!box1 || !box2) 
            return false;
        
        const left1 = box1.x;
        const right1 = box1.x + box1.w - 1;
        const top1 = box1.y;
        const bottom1 = box1.y + box1.h - 1;

        const left2 = box2.x;
        const right2 = box2.x + box2.w - 1;
        const top2 = box2.y;
        const bottom2 = box2.y + box2.h - 1;

        return (
            !(left1 > right2 || right1 < left2) &&
            !(bottom1 < top2 || top1 > bottom2)
        );
    }

    const isCardColliding = (box = boundingBox) => {
        const cards = selectedTabData?.draftDetails?.visuals?.map(v => ({
            x: v.positionX,
            y: v.positionY,
            w: v.width,
            h: v.height
        })) ?? [];

        return cards.some(card => isBoxColliding(card, box));
    }

    const isCardCollidingWithCard = (oldCard, newCard) => {
        const cards = selectedTabData?.draftDetails?.visuals?.map(v => ({
            i: v.visualId,
            x: v.positionX,
            y: v.positionY,
            w: v.width,
            h: v.height
        })) ?? [];
        const otherCards = cards.filter(card => card.i !== oldCard.i);

        return otherCards.some(card => isBoxColliding(card, newCard));
    }

    const handlePlaceholderClick = (coordinateItem) => {
        if (isViewMode) return;
        openVisualCreation(coordinateItem)
    }



    const handleCardDragStop = (_, oldCard, newCard) => {
        if(JSON.stringify(oldCard) !==  JSON.stringify(newCard)){
            if (isViewMode || isCardCollidingWithCard(oldCard, newCard)) {
                dashboardLib.editVisualCoordinates(oldCard.x, oldCard.y, oldCard)
                return;
            }
            dashboardLib.editVisualCoordinates(oldCard.x, oldCard.y, newCard)
        }
        else{
            return;
        }
    }

    const handleCardResizeStart = (_, oldCard) => {
        setIsDragging(oldCard.i);
    };

    const handleCardResizeStop = (_, oldCard, newCard) => {
        if (isViewMode || isCardCollidingWithCard(oldCard, newCard)) {
            dashboardLib.editVisualCoordinates(oldCard.x, oldCard.y, oldCard)
            setIsDragging(false);
            return;
        }

        dashboardLib.editVisualCoordinates(oldCard.x, oldCard.y, newCard)
        setIsDragging(false);
    }

    const handlePlaceholderMouseDown = (coordinateItem) => {
        if (isViewMode) return;
        setStartingCoordinates(coordinateItem)
        setHoverCoordinates(coordinateItem);
    }

    const handlePlaceholderMouseOver = (coordinateItem) => {
        if (isViewMode || !startingCoordinates) return;
        if (hoverCoordinates.x !== coordinateItem.x || hoverCoordinates.y !== coordinateItem.y) {
            setHoverCoordinates(coordinateItem);
        }
    }

    const handlePlaceholderMouseUp = () => {
        if (isViewMode || !startingCoordinates) return;
        if (startingCoordinates) {
            setStartingCoordinates(null);
            setHoverCoordinates(null);
            if (!isCardColliding()) {
                openVisualCreation(boundingBox);
            }
        }
    }

    return {
        boundingBox,
        isBoxColliding,
        isDragging,
        handlePlaceholderClick,
        handleCardDragStop,
        handleCardResizeStart,
        handleCardResizeStop,
        handlePlaceholderMouseDown,
        handlePlaceholderMouseOver,
        handlePlaceholderMouseUp,
    }
}